import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { WhatsAppOutlined, ShoppingOutlined } from '@ant-design/icons';
import { getMisPedidos } from '../../../store/MSCommerce/Pedidos/thunks';
import type { PedidoResponse } from '../../../store/MSCommerce/Pedidos/types';
import { isAuthenticated } from '../../../Services/auth';
import Contact from '../../../Services/contact';
import { crearMensajePedidoWhatsapp } from '../../../Services/pedidoUtils';
import styles from './MisReservas.module.scss';

const ESTADO_LABEL: Record<string, string> = {
  PENDIENTE: 'Pendiente',
  EN_REVISION: 'Comprobante subido',
  CONFIRMADO: 'Confirmada',
  ENTREGADO: 'Entregada',
  RECHAZADO: 'Rechazada',
  CANCELADO: 'Cancelada',
};

const ESTADO_AYUDA: Record<string, string> = {
  PENDIENTE: 'Estamos procesando tu reserva.',
  EN_REVISION: 'Estamos revisando tu comprobante de pago. Te avisaremos por WhatsApp cuando lo confirmemos.',
  CONFIRMADO: '¡Tu pago fue confirmado! Coordinaremos contigo la entrega por WhatsApp.',
  ENTREGADO: 'Este pedido ya fue entregado. ¡Gracias por tu compra!',
  RECHAZADO: 'El comprobante de este pedido fue rechazado. Escríbenos por WhatsApp si tienes dudas.',
  CANCELADO: 'Cancelaste esta reserva.',
};

const getCodigoCorto = (id: string) => `GS-${id.slice(0, 8).toUpperCase()}`;

const MisReservas: React.FC = () => {
  const dispatch = useDispatch<any>();
  const history = useHistory();

  const [pedidos, setPedidos] = useState<PedidoResponse[]>([]);
  const [cargando, setCargando] = useState(true);

  const cargar = async () => {
    setCargando(true);
    const data = await dispatch(getMisPedidos());
    setPedidos(data ?? []);
    setCargando(false);
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      history.replace('/login');
      return;
    }
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!isAuthenticated()) return null;

  return (
    <div className={styles.misReservas}>
      <div className={styles.misReservas__header}>
        <p className={styles.misReservas__subtitle}>GLOW SKIN</p>
        <h1 className={styles.misReservas__title}>Mis reservas</h1>
      </div>

      <div className={styles.misReservas__list}>
        {!cargando && pedidos.length === 0 ? (
          <div>
            <p className={styles.misReservas__empty}>Todavía no tienes reservas.</p>
            <button className={styles.misReservas__browseBtn} onClick={() => history.push('/products')}>
              <ShoppingOutlined /> Ver productos
            </button>
          </div>
        ) : (
          pedidos.map(pedido => {
            const codigo = getCodigoCorto(pedido.id);
            const pagoActual = pedido.pagos.find(p => p.status === 'EN_REVISION')
              ?? pedido.pagos[pedido.pagos.length - 1];
            const mensajeWhatsapp = crearMensajePedidoWhatsapp(pedido);

            return (
              <div key={pedido.id} className={styles.misReservas__card}>
                <div className={styles.misReservas__cardTop}>
                  <div>
                    <span className={styles.misReservas__codigo}>{codigo}</span>
                    <span className={`${styles.misReservas__estado} ${styles[`misReservas__estado--${pedido.status?.toLowerCase()}`]}`}>
                      {pedido.status ? (ESTADO_LABEL[pedido.status] ?? pedido.status) : ''}
                    </span>
                  </div>
                  <span className={styles.misReservas__fecha}>
                    {new Date(pedido.createdAt).toLocaleString('es-EC', { dateStyle: 'medium', timeStyle: 'short' })}
                  </span>
                </div>

                <div className={styles.misReservas__cardBody}>
                  <ul className={styles.misReservas__productos}>
                    {pedido.detalles.map((d, i) => (
                      <li key={i}>{d.nombreProducto} × {d.cantidad} — ${d.total.toFixed(2)}</li>
                    ))}
                  </ul>
                  <p className={styles.misReservas__total}>Total: ${pedido.total.toFixed(2)}</p>

                  {pedido.status && ESTADO_AYUDA[pedido.status] && (
                    <p className={styles.misReservas__estadoHelp}>{ESTADO_AYUDA[pedido.status]}</p>
                  )}

                  {pagoActual?.urlComprobante && (
                    <img
                      className={styles.misReservas__comprobante}
                      src={pagoActual.urlComprobante}
                      alt="Comprobante de pago"
                      loading="lazy"
                      onClick={() => window.open(pagoActual.urlComprobante!, '_blank')}
                    />
                  )}
                </div>

                <div className={styles.misReservas__cardActions}>
                  <a
                    className={styles.misReservas__whatsappBtn}
                    href={`https://wa.me/${Contact.WHATSAPP_NUMERO}?text=${mensajeWhatsapp}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <WhatsAppOutlined /> Escríbenos por WhatsApp
                  </a>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default MisReservas;
